// src/hooks/useDeviceSelection.ts
import type { Dispatch } from 'react'
import { createContext, useReducer } from 'react'

// Define a type for device data we want to store
export interface DeviceData {
  uid: string
  type: string
  serial?: string
  label?: string
}

// Define the state shape for selected devices
export interface SelectedDevicesState {
  // Map of selected device UIDs to their data
  selectedDevices: Record<string, DeviceData>
}

// Define possible actions
export type SelectedDevicesAction =
  | { type: 'SELECT_DEVICE'; device: DeviceData }
  | { type: 'DESELECT_DEVICE'; uid: string }
  | { type: 'SELECT_MULTIPLE_DEVICES'; devices: DeviceData[] }
  | { type: 'DESELECT_MULTIPLE_DEVICES'; uids: string[] }
  | { type: 'CLEAR_SELECTION' }

// Initial state
export const initialSelectedDevicesState: SelectedDevicesState = {
  selectedDevices: {},
}

// Reducer function
export const selectedDevicesReducer = (
  state: SelectedDevicesState,
  action: SelectedDevicesAction,
): SelectedDevicesState => {
  switch (action.type) {
    case 'SELECT_DEVICE': {
      return {
        ...state,
        selectedDevices: {
          ...state.selectedDevices,
          [action.device.uid]: action.device,
        },
      }
    }
    case 'DESELECT_DEVICE': {
      // Create a copy of the state
      const newState = { ...state }
      // Create a copy of the selectedDevices object
      const newSelectedDevices = { ...newState.selectedDevices }
      // Remove the deselected device
      delete newSelectedDevices[action.uid]
      return {
        ...newState,
        selectedDevices: newSelectedDevices,
      }
    }
    case 'SELECT_MULTIPLE_DEVICES': {
      // Create a new object with all existing selections plus new ones
      const newSelections = action.devices.reduce(
        (acc, device) => {
          acc[device.uid] = device
          return acc
        },
        { ...state.selectedDevices },
      )
      return {
        ...state,
        selectedDevices: newSelections,
      }
    }
    case 'DESELECT_MULTIPLE_DEVICES': {
      // Create a copy of the selectedDevices
      const newSelectedDevices = { ...state.selectedDevices }
      // Remove each deselected device
      for (const uid of action.uids) {
        delete newSelectedDevices[uid]
      }
      return {
        ...state,
        selectedDevices: newSelectedDevices,
      }
    }
    case 'CLEAR_SELECTION': {
      return {
        ...state,
        selectedDevices: {},
      }
    }
    default:
      return state
  }
}

// Create context for providing state across components
export interface SelectedDevicesContextType {
  state: SelectedDevicesState
  dispatch: Dispatch<SelectedDevicesAction>
}

export const SelectedDevicesContext = createContext<SelectedDevicesContextType>(
  {
    state: initialSelectedDevicesState,
    dispatch: () => null,
  },
)

// Custom hook for device selection
export const useDeviceSelection = () => {
  return useReducer(selectedDevicesReducer, initialSelectedDevicesState)
}

// Helper functions for common operations
export const deviceSelectionHelpers = {
  getSelectedDevicesArray: (state: SelectedDevicesState): DeviceData[] =>
    Object.values(state.selectedDevices),

  getSelectedDeviceIds: (state: SelectedDevicesState): string[] =>
    Object.keys(state.selectedDevices),

  isDeviceSelected: (state: SelectedDevicesState, uid: string): boolean =>
    uid in state.selectedDevices,

  getSelectedDeviceCount: (state: SelectedDevicesState): number =>
    Object.keys(state.selectedDevices).length,
}
